import type { CanvasVariant, VariantAxis } from "../canvas/variant-resolver";
import { variantDisplayPrice, type DisplayPrice } from "../canvas/variant-price";
import { DEFAULT_CURRENCY } from "@configs/currencies";
import { offeredSizes, type ArtworkSource, type OfferedSize } from "./artwork-sizing";
import type { Artwork, ArtworkKind, LocalizedText } from "./artwork-types";

/**
 * Sets: two or three works chosen to hang together, bought in one go.
 *
 * A set is not a product of its own. It is a shortcut: the same pieces the
 * gallery sells, at their normal prices, in one finish and one size so they
 * match on the wall. Each piece goes into the cart as its own line, exactly as
 * if the customer had added it from its own page, so nothing downstream
 * (checkout, the print queue, the server's size check) knows sets exist.
 *
 * The list lives in code rather than in the admin because it is small, chosen
 * by hand, and every entry has to be checked against the catalogue anyway: a
 * set is only worth showing when its works share a shape and a printable
 * size. art-sets.test.ts holds each one to that against a snapshot of the
 * catalogue, and the page re-checks it live — a set whose piece is retired or
 * can no longer be printed simply stops being offered.
 */

export type SetRoomScene = "sofa" | "bed" | "plain";

export interface ArtSet {
  slug: string;
  /** Every shop locale: sl, hr, sk, pl, cs, de-AT, en, it. */
  title: LocalizedText;
  intro: LocalizedText;
  /** Two or three works, in the order they hang, left to right. */
  artworkSlugs: string[];
  /** The room the page opens on. Sofa unless a set suits a bedroom better. */
  roomScene?: SetRoomScene;
}

/** The locales every set must be written in. */
export const SET_LOCALES = ["sl", "hr", "sk", "pl", "cs", "de-AT", "en", "it"] as const;

/** Wall between neighbouring pieces, in centimetres. */
export const SET_GAP_CM = 8;

export const ART_SETS: ArtSet[] = [
  {
    slug: "japonska-trojica",
    title: {
      sl: "Japonska trojica",
      hr: "Japanski trio",
      sk: "Japonské trio",
      pl: "Japońskie trio",
      cs: "Japonské trio",
      "de-AT": "Japanisches Trio",
      en: "Japanese trio",
      it: "Trio giapponese",
    },
    intro: {
      sl: "Hokusaijev Veliki val med Rdečim Fudžijem in Hiroshigejevo jesensko luno. Trije lesorezi v enaki velikosti, ki skupaj napolnijo steno nad kavčem.",
      hr: "Hokusaijev Veliki val između Crvenog Fujija i Hiroshigeova jesenskog mjeseca. Tri drvoreza u istoj veličini koji zajedno ispune zid iznad kauča.",
      sk: "Hokusaiova Veľká vlna medzi Červenou Fudži a Hirošigeho jesenným mesiacom. Tri drevorezy v rovnakej veľkosti, ktoré spolu zaplnia stenu nad gaučom.",
      pl: "Wielka fala Hokusaia między Czerwoną Fudżi a jesiennym księżycem Hiroshigego. Trzy drzeworyty w tym samym rozmiarze, które razem wypełnią ścianę nad kanapą.",
      cs: "Hokusaiova Velká vlna mezi Rudou Fudži a Hirošigeho podzimním měsícem. Tři dřevořezy ve stejné velikosti, které spolu zaplní stěnu nad pohovkou.",
      "de-AT": "Hokusais Große Welle zwischen dem Roten Fuji und Hiroshiges Herbstmond. Drei Holzschnitte in derselben Größe, die gemeinsam die Wand über dem Sofa füllen.",
      en: "Hokusai's Great Wave between Red Fuji and Hiroshige's autumn moon. Three woodblock prints in one size that together fill the wall above a sofa.",
      it: "La Grande onda di Hokusai tra il Fuji rosso e la luna d'autunno di Hiroshige. Tre xilografie della stessa misura che insieme riempiono la parete sopra il divano.",
    },
    artworkSlugs: [
      "hokusai-rdeci-fudzi",
      "hokusai-veliki-val-pri-kanagavi",
      "hiroshige-autumn-full-moon-at-ishiyama-temple",
    ],
  },
  {
    slug: "van-gogh-trojica",
    title: {
      sl: "Van Goghova trojica",
      hr: "Van Goghov trio",
      sk: "Van Goghovo trio",
      pl: "Trio Van Gogha",
      cs: "Van Goghovo trio",
      "de-AT": "Van-Gogh-Trio",
      en: "Van Gogh trio",
      it: "Trio di Van Gogh",
    },
    intro: {
      sl: "Perunike, Zvezdna noč in Mandljev cvet, vse iz zadnjih dveh let njegovega življenja. Trije Van Goghi, ki se drug ob drugem lepo ujamejo.",
      hr: "Perunike, Zvjezdana noć i Cvjetovi badema, sve iz posljednje dvije godine njegova života. Tri Van Gogha koja se jedan uz drugoga lijepo slažu.",
      sk: "Kosatce, Hviezdna noc a Mandľový kvet, všetky z posledných dvoch rokov jeho života. Tri Van Goghove obrazy, ktoré spolu vedľa seba krásne ladia.",
      pl: "Irysy, Gwiaździsta noc i Kwitnący migdałowiec, wszystkie z ostatnich dwóch lat jego życia. Trzy obrazy Van Gogha, które pięknie współgrają obok siebie.",
      cs: "Kosatce, Hvězdná noc a Mandloňový květ, všechny z posledních dvou let jeho života. Tři Van Goghovy obrazy, které spolu vedle sebe krásně ladí.",
      "de-AT": "Schwertlilien, Sternennacht und Mandelblüte, alle aus seinen letzten beiden Lebensjahren. Drei Van Goghs, die nebeneinander wunderbar harmonieren.",
      en: "Irises, The Starry Night and Almond Blossom, all from the last two years of his life. Three Van Goghs that sit beautifully side by side.",
      it: "Iris, Notte stellata e Mandorlo in fiore, tutti degli ultimi due anni della sua vita. Tre Van Gogh che stanno benissimo uno accanto all'altro.",
    },
    artworkSlugs: ["van-gogh-irises", "van-gogh-zvezdna-noc", "van-gogh-mandljev-cvet"],
  },
  {
    slug: "umirjene-krajine",
    title: {
      sl: "Umirjene krajine",
      hr: "Mirni krajolici",
      sk: "Pokojné krajiny",
      pl: "Spokojne pejzaże",
      cs: "Klidné krajiny",
      "de-AT": "Ruhige Landschaften",
      en: "Quiet landscapes",
      it: "Paesaggi tranquilli",
    },
    intro: {
      sl: "Gozdni ribnik, jesenski dan in morska obala. Tri tihe krajine Corota, Innessa in Boudina v mehkih barvah, ob katerih si oko odpočije.",
      hr: "Šumski ribnjak, jesenski dan i morska obala. Tri tiha krajolika Corota, Innessa i Boudina u mekim bojama, uz koje se oko odmori.",
      sk: "Lesný rybník, jesenný deň a morské pobrežie. Tri tiché krajiny od Corota, Innessa a Boudina v jemných farbách, pri ktorých si oko oddýchne.",
      pl: "Leśny staw, jesienny dzień i morskie wybrzeże. Trzy ciche pejzaże Corota, Innessa i Boudina w miękkich barwach, przy których oko odpoczywa.",
      cs: "Lesní rybník, podzimní den a mořské pobřeží. Tři tiché krajiny od Corota, Innesse a Boudina v jemných barvách, u kterých si oko odpočine.",
      "de-AT": "Ein Waldteich, ein Herbsttag und eine Meeresküste. Drei stille Landschaften von Corot, Inness und Boudin in sanften Farben, bei denen das Auge zur Ruhe kommt.",
      en: "A woodland pond, an autumn day and a stretch of coast. Three calm landscapes by Corot, Inness and Boudin in soft colours that rest the eye.",
      it: "Uno stagno nel bosco, una giornata d'autunno e una costa sul mare. Tre paesaggi quieti di Corot, Inness e Boudin, dai colori morbidi che riposano lo sguardo.",
    },
    artworkSlugs: [
      "corot-the-pond-at-the-entrance-of-the-woods",
      "inness-sunny-autumn-day",
      "boudin-the-coast-at-trouville",
    ],
  },
  {
    slug: "cvetlicni-par",
    title: {
      sl: "Cvetlični par",
      hr: "Cvjetni par",
      sk: "Kvetinový pár",
      pl: "Kwiatowa para",
      cs: "Květinový pár",
      "de-AT": "Blumenpaar",
      en: "Flower pair",
      it: "Coppia floreale",
    },
    intro: {
      sl: "Redonova vaza s cvetjem in Monetovo spomladansko cvetje. Dve pokončni tihožitji, ki prineseta pomlad v spalnico ali jedilnico.",
      hr: "Redonova vaza s cvijećem i Monetovo proljetno cvijeće. Dvije uspravne mrtve prirode koje unose proljeće u spavaću sobu ili blagovaonicu.",
      sk: "Redonova váza s kvetmi a Monetove jarné kvety. Dve zvislé zátišia, ktoré prinesú jar do spálne alebo jedálne.",
      pl: "Wazon z kwiatami Redona i wiosenne kwiaty Moneta. Dwie pionowe martwe natury, które wniosą wiosnę do sypialni lub jadalni.",
      cs: "Redonova váza s květinami a Monetovy jarní květiny. Dvě svislá zátiší, která přinesou jaro do ložnice nebo jídelny.",
      "de-AT": "Redons Blumenvase und Monets Frühlingsblumen. Zwei Stillleben im Hochformat, die den Frühling ins Schlafzimmer oder Esszimmer bringen.",
      en: "Redon's vase of flowers and Monet's spring flowers. Two portrait still lifes that bring spring into a bedroom or dining room.",
      it: "Il vaso di fiori di Redon e i fiori di primavera di Monet. Due nature morte verticali che portano la primavera in camera o in sala da pranzo.",
    },
    artworkSlugs: ["redon-vase-of-flowers", "monet-spring-flowers"],
    roomScene: "bed",
  },
  {
    slug: "crno-beli-yosemite",
    title: {
      sl: "Črno-beli Yosemite",
      hr: "Crno-bijeli Yosemite",
      sk: "Čiernobiely Yosemite",
      pl: "Czarno-białe Yosemite",
      cs: "Černobílé Yosemite",
      "de-AT": "Yosemite in Schwarz-Weiß",
      en: "Yosemite in black and white",
      it: "Yosemite in bianco e nero",
    },
    intro: {
      sl: "Dve fotografiji Carletona Watkinsa iz 19. stoletja: El Capitan in Spodnji slapovi Yosemita. Pokončen par za mirno steno v spalnici ali na hodniku.",
      hr: "Dvije fotografije Carletona Watkinsa iz 19. stoljeća: El Capitan i Donji slapovi Yosemitea. Uspravan par za miran zid u spavaćoj sobi ili hodniku.",
      sk: "Dve fotografie Carletona Watkinsa z 19. storočia: El Capitan a Dolné Yosemitské vodopády. Zvislý pár na pokojnú stenu v spálni alebo na chodbe.",
      pl: "Dwie fotografie Carletona Watkinsa z XIX wieku: El Capitan i Dolny wodospad Yosemite. Pionowa para na spokojną ścianę w sypialni lub przedpokoju.",
      cs: "Dvě fotografie Carletona Watkinse z 19. století: El Capitan a Dolní Yosemitský vodopád. Svislý pár na klidnou stěnu v ložnici nebo na chodbě.",
      "de-AT": "Zwei Fotografien von Carleton Watkins aus dem 19. Jahrhundert: El Capitan und die unteren Yosemite-Fälle. Ein Paar im Hochformat für eine ruhige Wand im Schlafzimmer oder Vorzimmer.",
      en: "Two 19th-century photographs by Carleton Watkins: El Capitan and Lower Yosemite Falls. A portrait pair for a quiet wall in a bedroom or hallway.",
      it: "Due fotografie ottocentesche di Carleton Watkins: El Capitan e le cascate inferiori di Yosemite. Una coppia verticale per una parete tranquilla in camera o nell'ingresso.",
    },
    artworkSlugs: ["watkins-el-capitan-yosemite", "watkins-lower-yosemite-falls"],
    roomScene: "bed",
  },
];

export const findSet = (slug: string | null | undefined): ArtSet | undefined =>
  slug ? ART_SETS.find((s) => s.slug === slug) : undefined;

/** The sets a work hangs in, in list order. */
export const setsContaining = (artworkSlug: string): ArtSet[] =>
  ART_SETS.filter((s) => s.artworkSlugs.includes(artworkSlug));

/** Where a set lives. Sets sit under the art storefront whatever their works are. */
export const setPath = (locale: string, slug: string) => `/${locale}/art/komplet/${slug}`;

/** Where one piece of a set lives: photographs have their own storefront. */
export const artworkPath = (locale: string, artwork: Pick<Artwork, "kind" | "slug">) =>
  `/${locale}/${storefrontOf(artwork.kind)}/${artwork.slug}`;

export const storefrontOf = (kind: ArtworkKind) => (kind === "PHOTO" ? "photography" : "art");

/**
 * The set's works, in hanging order, from whatever catalogue rows are at hand.
 *
 * `missing` lists slugs that are not in the catalogue or are switched off. A
 * set with anything missing is not offered: selling two thirds of a triptych
 * under the triptych's name would be the half-add the set exists to avoid.
 */
export function resolveSet(
  set: ArtSet,
  catalogue: ReadonlyArray<Artwork> | null | undefined,
): { pieces: Artwork[]; missing: string[] } {
  const bySlug = new Map((catalogue ?? []).map((a) => [a.slug, a]));
  const pieces: Artwork[] = [];
  const missing: string[] = [];
  for (const slug of set.artworkSlugs) {
    const artwork = bySlug.get(slug);
    if (artwork && artwork.active !== false) pieces.push(artwork);
    else missing.push(slug);
  }
  return { pieces, missing };
}

/** One size the whole set can be printed at, with each piece's own trim. */
export interface SetSize {
  variant: CanvasVariant;
  /** Same order as the pieces; each turned to suit its own work. */
  pieces: OfferedSize[];
}

/**
 * The sizes every piece can honestly be printed at, smallest first.
 *
 * The intersection of each piece's offeredSizes, by variant: one finish and
 * one size for the whole set is the point of a set, so a size one piece
 * cannot carry is not offered for any of them. A variant's print spec is
 * orientation-free, so a landscape and a portrait work can still share it;
 * each piece keeps its own turned trim for the wall preview.
 */
export function commonSizes(
  pieces: ReadonlyArray<ArtworkSource>,
  variants: CanvasVariant[] | null | undefined,
): SetSize[] {
  if (!pieces.length || !variants?.length) return [];
  const perPiece = pieces.map(
    (p) =>
      new Map(
        offeredSizes({ widthPx: p.widthPx, heightPx: p.heightPx }, variants).map((o) => [
          o.variant.id,
          o,
        ]),
      ),
  );
  return [...perPiece[0].values()]
    .filter((o) => perPiece.every((m) => m.has(o.variant.id)))
    .map((o) => ({ variant: o.variant, pieces: perPiece.map((m) => m.get(o.variant.id)!) }));
}

/**
 * What the set costs: the sum of what each piece costs on its own page.
 *
 * Summed per piece in the market's currency, so a złoty total is a sum of
 * złoty prices. If the pieces should ever disagree about the currency (one
 * has a market price, another only the euro base), the total falls back to
 * euros for all of them rather than adding numbers on different scales.
 */
export function setTotal(
  variants: ReadonlyArray<CanvasVariant>,
  currency: string,
): DisplayPrice | null {
  if (!variants.length) return null;
  const prices = variants.map((v) => variantDisplayPrice(v, currency));
  const same = new Set(prices.map((p) => p.currency)).size === 1;
  const parts = same
    ? prices
    : variants.map((v) => ({
        amount: v.price,
        currency: DEFAULT_CURRENCY,
        compareAt: typeof v.compareAtPrice === "number" ? v.compareAtPrice : null,
      }));
  const amount = parts.reduce((sum, p) => sum + p.amount, 0);
  const compareAt = parts.every((p) => p.compareAt != null)
    ? parts.reduce((sum, p) => sum + (p.compareAt ?? 0), 0)
    : null;
  return { amount, currency: parts[0].currency, compareAt };
}

/**
 * A variant re-priced as the whole set, for the size picker.
 *
 * The picker prints each size's price from the variant it is given. Handing
 * it a copy that costs `count` times as much makes every tile show what the
 * customer will actually pay for the set at that size, while the id stays the
 * real variant's, so a pick maps straight back. Every piece of a set is the
 * same variant, so this agrees with setTotal (tested).
 */
export function setPricedVariant(variant: CanvasVariant, count: number): CanvasVariant {
  const times = (n: number) => n * count;
  return {
    ...variant,
    price: times(variant.price),
    compareAtPrice:
      typeof variant.compareAtPrice === "number" ? times(variant.compareAtPrice) : variant.compareAtPrice,
    priceCurrencies: variant.priceCurrencies
      ? Object.fromEntries(
          Object.entries(variant.priceCurrencies).map(([k, v]) => [
            k,
            typeof v === "number" ? times(v) : v,
          ]),
        )
      : variant.priceCurrencies,
  };
}

/**
 * The size the page opens on: the shop's standard finish (the first value
 * each axis declares that the set can be sold in — canvas before paper,
 * stretched before rolled), at its smallest size. Same rule as a single
 * work's page, so a piece and its set open on the same thing.
 */
export function defaultSetSize(sizes: SetSize[], axes: VariantAxis[]): SetSize | null {
  if (!sizes.length) return null;
  const initial: Record<string, string> = {};
  const fits = (s: SetSize, wanted: Record<string, string>) =>
    Object.entries(wanted).every(([k, v]) => s.variant.axisValues?.[k] === v);
  for (const axis of axes) {
    if (axis.key === "size") continue;
    const preferred = axis.values.find((value) =>
      sizes.some((s) => fits(s, { ...initial, [axis.key]: value.value })),
    );
    if (preferred) initial[axis.key] = preferred.value;
  }
  return sizes.find((s) => fits(s, initial)) ?? sizes[0];
}

/** A set that can be sold right now: every work present and one shared size. */
export function sellableSet(
  set: ArtSet,
  catalogue: ReadonlyArray<Artwork> | null | undefined,
  variants: CanvasVariant[] | null | undefined,
): { pieces: Artwork[]; sizes: SetSize[] } | null {
  const { pieces, missing } = resolveSet(set, catalogue);
  if (missing.length) return null;
  const sizes = commonSizes(pieces, variants);
  return sizes.length ? { pieces, sizes } : null;
}

/** Set URLs for the sitemap, only for sets that can be bought. */
export function setSitemapUrls(
  origin: string,
  locale: string,
  catalogue: ReadonlyArray<Artwork>,
  variants: CanvasVariant[],
): Array<{ loc: string }> {
  return ART_SETS.filter((s) => sellableSet(s, catalogue, variants)).map((s) => ({
    loc: `${origin}${setPath(locale, encodeURIComponent(s.slug))}`,
  }));
}

/**
 * Where each piece hangs, in centimetres from the group's top-left corner.
 *
 * Side by side with SET_GAP_CM between them, centred on one horizontal line —
 * how a row of pictures is usually hung, so mixed heights share a middle.
 */
export function setWallLayout(
  trims: ReadonlyArray<{ widthMm: number; heightMm: number }>,
  gapCm = SET_GAP_CM,
): { widthCm: number; heightCm: number; pieces: Array<{ left: number; top: number; width: number; height: number }> } {
  const heightCm = Math.max(0, ...trims.map((t) => t.heightMm / 10));
  let x = 0;
  const pieces = trims.map((t, i) => {
    const width = t.widthMm / 10;
    const height = t.heightMm / 10;
    const piece = { left: x, top: (heightCm - height) / 2, width, height };
    x += width + (i < trims.length - 1 ? gapCm : 0);
    return piece;
  });
  return { widthCm: x, heightCm, pieces };
}

/** What one material offers across a set's shared sizes. */
export interface SetMaterialOption {
  material: string;
  /** The cheapest total for the set in this material. */
  from: DisplayPrice;
  /** Mount and surface values sold in this material, in first-seen order. */
  mounts: string[];
  surfaces: string[];
  /** Smallest and largest shared size in this material (trim of the first piece). */
  smallest: OfferedSize;
  largest: OfferedSize;
}

/** Paper first: the cheaper way in, and the order the shop names them in. */
const MATERIAL_ORDER = ["paper", "canvas"];

/**
 * The materials a set can be printed in, each with its "from" price for the
 * whole set and the options it comes with — read off the variants the set can
 * actually be sold as, so a card never promises a gloss or a frame that is
 * not there.
 */
export function setMaterialOptions(
  sizes: SetSize[],
  pieceCount: number,
  currency: string,
): SetMaterialOption[] {
  const byMaterial = new Map<string, SetSize[]>();
  for (const s of sizes) {
    const m = s.variant.axisValues?.material ?? "";
    byMaterial.set(m, [...(byMaterial.get(m) ?? []), s]);
  }
  const options: SetMaterialOption[] = [];
  for (const [material, list] of byMaterial) {
    const totals = list.map((s) => setTotal(Array(pieceCount).fill(s.variant), currency)!);
    // Cheapest in one denomination: the market's own when any size has it, so
    // a euro fallback on one size never undercuts złoty prices on the rest.
    const market = totals.some((p) => p.currency !== DEFAULT_CURRENCY)
      ? totals.filter((p) => p.currency !== DEFAULT_CURRENCY)
      : totals;
    const from = market.reduce((min, p) => (p.amount < min.amount ? p : min));
    const values = (key: string) => [
      ...new Set(list.map((s) => s.variant.axisValues?.[key]).filter((v): v is string => !!v)),
    ];
    const area = (s: SetSize) => s.pieces[0].widthMm * s.pieces[0].heightMm;
    const sorted = [...list].sort((a, b) => area(a) - area(b));
    options.push({
      material,
      from,
      mounts: values("mount"),
      surfaces: values("surface"),
      smallest: sorted[0].pieces[0],
      largest: sorted[sorted.length - 1].pieces[0],
    });
  }
  const rank = (m: string) => {
    const i = MATERIAL_ORDER.indexOf(m);
    return i === -1 ? MATERIAL_ORDER.length : i;
  };
  return options.sort((a, b) => rank(a.material) - rank(b.material));
}

/**
 * The distinct sizes a set comes in, smallest first, as the first piece's
 * trim — what "available from 60 × 40 to 70 × 50 cm" is read from.
 */
export function setSizeRange(sizes: SetSize[]): OfferedSize[] {
  const seen = new Map<string, OfferedSize>();
  for (const s of sizes) {
    const p = s.pieces[0];
    const key = `${p.widthMm}x${p.heightMm}`;
    if (!seen.has(key)) seen.set(key, p);
  }
  return [...seen.values()].sort((a, b) => a.widthMm * a.heightMm - b.widthMm * b.heightMm);
}
